import { OpeningHour } from '../../models/dealer.model'
import { sanitizeInput } from '../../utils/sanitize'

export function today() {
	const now = new Date().getDay()
	// Have to convert to 1, 2, 3, 4, 5, 6, 7 b/c those are the numbers DL uses. DL uses 7 for Sunday
	return now === 0 ? 7 : now
}

export function getDayOpenHours(openingHours: OpeningHour[], day: number): OpeningHour | undefined {
	const openTime = openingHours.find(h => h.dayOfWeek.includes(day))
	return openTime
}

export function getCurrentOpenHoursMrkup(openingHours: OpeningHour[], labelData: Record<string, string>, departmentCode: string): string {
	let hoursLabelMrkup = ''
	const todayHours: OpeningHour = getDayOpenHours(openingHours, today())
	let dlOpensAtLabel = 'Label Missing - Unk. Dept.'
	let dlClosingSoonAtLabel = 'Label Missing - Unk. Dept.'
	let dlOpenUntilLabel = 'Label Missing - Unk. Dept.'
	let dlClosedLabel = 'Label Missing - Unk. Dept.'
	let dlClosedTodayLabel = 'Label Missing - Unk. Dept.'

	switch (departmentCode) {
		case '1':
			dlOpensAtLabel = sanitizeInput(labelData?.salesOpensAtLabel) || 'Label Missing'
			dlClosingSoonAtLabel = sanitizeInput(labelData?.salesClosingSoonAtLabel) || 'Label Missing'
			dlOpenUntilLabel = sanitizeInput(labelData?.salesOpenUntilLabel) || 'Label Missing'
			dlClosedLabel = sanitizeInput(labelData?.salesClosedLabel) || 'Label Missing'
			dlClosedTodayLabel = sanitizeInput(labelData?.salesClosedTodayLabel) || 'Label Missing'
			break;
		case '2':
			dlOpensAtLabel = sanitizeInput(labelData?.serviceOpensAtLabel) || 'Label Missing'
			dlClosingSoonAtLabel = sanitizeInput(labelData?.serviceClosingSoonAtLabel) || 'Label Missing'
			dlOpenUntilLabel = sanitizeInput(labelData?.serviceOpenUntilLabel) || 'Label Missing'
			dlClosedLabel = sanitizeInput(labelData?.serviceClosedLabel) || 'Label Missing'
			dlClosedTodayLabel = sanitizeInput(labelData?.serviceClosedTodayLabel) || 'Label Missing'
			break;
		default:
			break;
	}

	if (todayHours) {
		const now = new Date()
		const openTime = new Date(`${now.toDateString()} ${formatTime(todayHours.openFrom)}`)
		const closeTime = new Date(`${now.toDateString()} ${formatTime(todayHours.openTo)}`)

		// If the closing time is less than the opening time, it is the following day
		if (openTime.getTime() > closeTime.getTime()) {
			closeTime.setDate(closeTime.getDate() + 1)
		}

		if (now < openTime) {
			hoursLabelMrkup = `<span class="closed">${dlOpensAtLabel} ${sanitizeInput(todayHours.openFrom)}</span>`
		} else if (now > openTime && now < closeTime) {
			if (closeTime.getTime() - now.getTime() < 60 * 60 * 1000) {
				hoursLabelMrkup = `<span class="closed">${dlClosingSoonAtLabel} ${sanitizeInput(todayHours.openTo)}</span>`
			} else {
				hoursLabelMrkup = `<span class="open">${dlOpenUntilLabel} ${todayHours.openTo}</span>`
			}
		} else if (now > closeTime) {
			hoursLabelMrkup = `<span class="closed">${dlClosedLabel}</span>`
		}
	} else {
		hoursLabelMrkup = `<span class="closed">${dlClosedTodayLabel}</span>`
	}

	return hoursLabelMrkup
}

function getDayNames(locale = 'en-US') {
	// Use a fixed date reference, ensuring consistent day ordering across locales.
	// We use dates starting from Sunday (e.g., Jan 4th, 2017 was a Wednesday) and
	// create dates for each day of the week to ensure all day names are captured.
	const days = [2, 3, 4, 5, 6, 7, 8].map(day => {
		return new Date(`2017-01-${day < 10 ? `0${day}` : day}T00:00:00Z`);
	});

	const formatter = new Intl.DateTimeFormat(locale, {
		weekday: 'short',
		timeZone: 'UTC', // Use UTC to avoid local timezone issues
	} as Intl.DateTimeFormatOptions);

	// The order in the resulting array depends on the order of dates created
	// (Sunday to Saturday if the base dates are correctly set).
	return days.map(date => formatter.format(date));
}

/**
 * Generates the markup for the hours of operation for each day of the week
 * @param openingHours
 * @param closedLabel
 */
export function getHoursMarkup(openingHours: OpeningHour[], closedLabel: string, language = 'en'): string {
	const DAY_ARRAY = getDayNames(language)

	const generatedHoursArray = Array.from({ length: 7 }, () => {
		return {
			openFrom: '',
			openTo: '',
		}
	})
	openingHours.forEach(h => {
		h.dayOfWeek.forEach(d => {
			generatedHoursArray[d - 1] = {
				openFrom: sanitizeInput(h.openFrom),
				openTo: sanitizeInput(h.openTo),
			}
		})
	})

	let mrkup = ''
	DAY_ARRAY.forEach((day, index) => {
		const hours = generatedHoursArray[index]
		mrkup += `
			<div class="day">
				<span class="day-abbr">${day}</span>
				<span class="day-hours">${hours.openFrom ? `${hours.openFrom} - ${hours.openTo}` : sanitizeInput(closedLabel)}</span>
			</div>
		`
	})

	return mrkup
}

/**
 * Converts time into 24-hour format
 * @param string timeStr
 * @returns 24-hour time string
 */
export function formatTime(timeStr: string) {
	if (timeStr.includes('am') || timeStr.includes('AM') || timeStr.includes('pm') || timeStr.includes('PM')) {
		let hours
		let minutes
		const modifier = timeStr.substring(timeStr.length - 2, timeStr.length)
		if (timeStr.includes(':')) {
			hours = timeStr.substring(0, timeStr.indexOf(':'))
			minutes = timeStr.substring(timeStr.indexOf(':') + 1, timeStr.length - 2)
		} else {
			hours = timeStr.substring(0, timeStr.length - 2)
			minutes = '00'
		}
		if (hours === '12') {
			hours = '00'
		}
		if (modifier.toLocaleLowerCase() === 'pm') {
			hours = parseInt(hours, 10) + 12
		}
		return hours + ':' + minutes
	}
	// Case for timeStr in 23h59 format
	else if (timeStr.includes('h')) {
		return timeStr.replace('h', ':')
	}
	return timeStr
}
